/* eslint-disable no-unused-vars */
/* eslint-disable react/no-array-index-key */
import React, { FC, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { DocumentType, MPSProduct, MPSService, StatementStatus } from "../../types";
import { StatementResponse } from "../../api/statement-service/types";
import { getStatement, getStatementPaylink, getStockAvailability } from "../../api/statement-service/requests";
import { setNavigateOptions, isService } from "../../utils/utils";
import IkhokhaLoader from "../../assets/iK-Load-Animation.gif";
import { isHttpErrorResponse } from "../../api/util";
import StatementDesktop from "./desktop";
import StatementMobile from "./mobile";

import styles from "./index.module.css";

const UNAVAILABLE_STOCK_CODE = "prd_20";

const normalizeMissingProductIds = (products?: string[]) => {
  if (!products?.length) {
    return [];
  }

  return Array.from(
    new Set(
      products
        .flatMap(product => product.split(","))
        .map(productId => productId.trim())
        .filter(productId => productId.length > 0),
    ),
  );
};

const getProductStockId = (product: MPSProduct) => {
  return product.variant?.variantID ?? product.catalogueItemId;
};

const getProductLookupIds = (product: MPSProduct) => {
  const ids = [product.catalogueItemId];
  if (product.variant?.variantID) {
    ids.push(product.variant.variantID);
  }
  if (product.variant?.catalogueItemID) {
    ids.push(product.variant.catalogueItemID);
  }
  return ids;
};

const isMissingProduct = (product: MPSProduct, missingProductIds: Set<string>) => {
  return getProductLookupIds(product).some(id => missingProductIds.has(id));
};

const Statement: FC = () => {
  const { statement } = useParams();
  const navigate = useNavigate();
  const [statementResponse, setStatementResponse] = useState<StatementResponse>();
  const [products, setProducts] = useState<MPSProduct[]>([]);
  const [services, setServices] = useState<MPSService[]>([]);
  const [missingProductIds, setMissingProductIds] = useState<string[]>([]);
  const [isStockAvailabilityLoading, setIsStockAvailabilityLoading] = useState(false);
  const isPaid = useRef(false);
  const [loadPaylink, setLoadPaylink] = useState(false);
  const isInvoicePaid = statementResponse?.statement?.status === StatementStatus.PAID;
  const missingProductIdsSet = new Set(missingProductIds);
  const hasBlockingMissingItems = !isInvoicePaid && products.some(product => isMissingProduct(product, missingProductIdsSet));

  const generatePaylinkAndRedirect = async (id: string, docType: DocumentType) => {
    if (hasBlockingMissingItems || isStockAvailabilityLoading) {
      return;
    }

    setLoadPaylink(true);
    const response = await getStatementPaylink(id, docType);

    if (isHttpErrorResponse(response)) {
      const paylinkErrorHeader = "Payment Failed";
      const paylinkErrorMessage = "We were unable to complete transaction, please try again";
      navigate(`/error`, {
        state: setNavigateOptions(true, id, paylinkErrorHeader, paylinkErrorMessage),
      });
      return;
    }

    if (response) {
      window.location.href = response.tender.reference;
    }
  };

  const getMPSFromProduct = () => {
    if (!statementResponse) return;

    const mpsProducts: MPSProduct[] = statementResponse.statement.productList
      .filter(item => !isService(item))
      .map(product => {
        return {
          catalogueItemId: product.catalogueItemId,
          name: product.name,
          description: product.description,
          quantity: product.quantity,
          price: product.price,
          vat: product.vat,
          variant: product.variant,
          category: product.category,
        };
      });
    setProducts(mpsProducts);
    const mpsServices: MPSService[] = statementResponse.statement.productList
      .filter(item => isService(item))
      .map(service => {
        return {
          catalogueItemId: service.catalogueItemId,
          category: service.category,
          name: service.name,
          description: service.description,
          quantity: service.quantity,
          price: service.price,
          vat: service.vat,
          duration: service.duration ?? 0,
        };
      });
    setServices(mpsServices);
  };

  useEffect(() => {
    if (!statementResponse?.statement?.productList.length) return;
    getMPSFromProduct();
  }, [statementResponse]);

  useEffect(() => {
    const owner = statementResponse?.statement?.ownerId;
    if (!owner || isInvoicePaid) {
      setMissingProductIds([]);
      setIsStockAvailabilityLoading(false);
      return;
    }

    const productQtyById = products.reduce<Record<string, number>>((acc, product) => {
      const stockId = getProductStockId(product);
      if (!stockId) {
        return acc;
      }

      acc[stockId] = (acc[stockId] ?? 0) + Number(product.quantity ?? 0);
      return acc;
    }, {});

    const idList = Object.entries(productQtyById)
      .filter(([, qty]) => qty > 0)
      .map(([id, qty]) => `${id};${qty}`)
      .join(",");

    if (!idList) {
      setMissingProductIds([]);
      setIsStockAvailabilityLoading(false);
      return;
    }

    let isCancelled = false;
    const fetchMissingProducts = async () => {
      setIsStockAvailabilityLoading(true);
      try {
        const response = await getStockAvailability(owner, idList);
        if (isCancelled) {
          return;
        }

        if (isHttpErrorResponse(response)) {
          setMissingProductIds([]);
          return;
        }

        if (response.responseCode?.trim().toLowerCase() === UNAVAILABLE_STOCK_CODE) {
          const responseMissingIds = normalizeMissingProductIds(
            response.products?.length ? response.products : response.unavailable?.map(item => item.productId),
          );
          const productIdSet = products.reduce((acc, product) => {
            getProductLookupIds(product).forEach(id => acc.add(id));
            return acc;
          }, new Set<string>());
          const matchedMissingIds = responseMissingIds.filter(id => productIdSet.has(id));

          setMissingProductIds(Array.from(new Set(matchedMissingIds)));
          return;
        }

        setMissingProductIds([]);
      } catch {
        if (!isCancelled) {
          setMissingProductIds([]);
        }
      } finally {
        if (!isCancelled) {
          setIsStockAvailabilityLoading(false);
        }
      }
    };

    fetchMissingProducts();
    return () => {
      isCancelled = true;
    };
  }, [isInvoicePaid, products, statementResponse?.statement?.ownerId]);

  // Make a network call to get the statement information
  useEffect(() => {
    (async () => {
      if (statement) {
        const response = await getStatement(statement, DocumentType.INVOICE);
        if (response.success) {
          setStatementResponse(response.statementResponse);
          isPaid.current = response.statementResponse.statement.status === StatementStatus.PAID;
        }
      }
    })();
  }, []);

  if (statementResponse && !loadPaylink) {
    return (
      <div>
        {/*............... desktop ...............*/}
        <div className={styles.desktop}>
          <StatementDesktop
            statement={statementResponse}
            onPayInvoice={generatePaylinkAndRedirect}
            products={products}
            services={services}
            missingProductIds={missingProductIds}
            hasBlockingMissingItems={hasBlockingMissingItems}
            isStockAvailabilityLoading={isStockAvailabilityLoading}
          />
        </div>

        {/*............... mobile ...............*/}
        <div className={styles.mobile}>
          <StatementMobile
            statement={statementResponse}
            onPayInvoice={generatePaylinkAndRedirect}
            products={products}
            services={services}
            missingProductIds={missingProductIds}
            hasBlockingMissingItems={hasBlockingMissingItems}
            isStockAvailabilityLoading={isStockAvailabilityLoading}
          />
        </div>
      </div>
    );
  }
  return (
    <div className={`${styles.card} d-flex justify-content-center align-items-center h-100-vh`}>
      <div>
        <img src={IkhokhaLoader} alt="loader" />
      </div>
    </div>
  );
};

export default Statement;
